import React from "react";
import { useNavigate } from "react-router-dom";
import { ESolarServiceProvider } from "../../../../shared/oversight-core/enums/solar-service-provider";
import { AppRoute } from "../../../../shared/oversight-core/interfaces/app-routes";
import { IMapLocation } from "../../../../shared/oversight-core/interfaces/map-location";
import { IShallowSolarPlantView } from "../../../../shared/oversight-core/interfaces/shallow-solar-plant-view";
import LeafletMapDetails from "../../../../shared/oversight-core/shared-components/leaflet-map-details/leaflet-map-details";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Pagination from "../../../../shared/oversight-core/ui-elements/pagination/pagination";
import { getSiteStatusInfo } from "../../../../shared/oversight-core/utils/get-site-status-info";
import styles from "./sites-map-view.module.scss";

interface ISitesMapViewProps {
  solarPlantList: IShallowSolarPlantView[];
  selectedSolarPlant?: IShallowSolarPlantView;
  setSelectedSolarPlant: (plant: IShallowSolarPlantView) => void;
  getServiceProvider: (id: string) => ESolarServiceProvider;
  totalElements: number;
  pageNumber: number;
  onPageChange: (pageNumber: number) => void;
  locations: IMapLocation[];
}

const SitesMapView: React.FC<ISitesMapViewProps> = ({
  solarPlantList,
  selectedSolarPlant,
  setSelectedSolarPlant,
  getServiceProvider,
  totalElements,
  pageNumber,
  onPageChange,
  locations,
}) => {
  const navigate = useNavigate();

  const openSite = (plant: IShallowSolarPlantView) => {
    navigate(AppRoute.ADMIN_SITES + "/" + plant.plantId, {
      state: {
        id: plant.plantId,
        serviceProvider: getServiceProvider(plant.generationServiceProviderId),
        name: plant.siteAlias || plant.plantName,
      },
    });
  };

  return (
    <div className={`${styles.sitesMapView} mt-4`}>
      <div className={styles.mapWrapper}>
        <div className={styles.mapSidebar}>
          <div className={styles.sidebarHeader}>
            <div>
              <h6 className={styles.headerTitle}>Sites Map View</h6>
            </div>
            <MaterialIcon icon="sunny" color="#64748B" size={18} />
          </div>

          <div className={styles.sitesListContainer}>
            {solarPlantList.map((s) => {
              const statusInfo = getSiteStatusInfo(s);
              const isSelected = selectedSolarPlant?.plantId === s.plantId;
              const siteName = s.siteAlias ? s.siteAlias : s.plantName;
              const openHintId = `site-open-hint-${s.plantId}`;
              return (
                <React.Fragment key={s.plantId}>
                  <button
                    type="button"
                    className={`${styles.siteListItem} ${
                      isSelected ? styles.siteListItemSelected : ""
                    }`}
                    aria-pressed={isSelected}
                    aria-describedby={openHintId}
                    onClick={() => setSelectedSolarPlant(s)}
                    onDoubleClick={() => openSite(s)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        openSite(s);
                      }
                    }}
                  >
                    <div className={styles.siteItemHeader}>
                      <div className={styles.siteItemTitle}>
                        <div className="d-flex align-items-center gap-1">
                          <MaterialIcon
                            icon="location_on"
                            color="#64748b"
                            size={14}
                          />
                          <span>{siteName}</span>
                        </div>
                        <div className={styles.siteItemZone}>
                          {s.address || "WESTERN ZONE"}
                        </div>
                      </div>
                      <div
                        className={styles.siteStatusBadge}
                        style={{
                          backgroundColor: statusInfo.bgColor,
                          color: statusInfo.color,
                        }}
                      >
                        {statusInfo.label}
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2 font-size-11 text-muted">
                      <span>
                        Capacity: {s.capacity ? `${s.capacity} kW` : "N/A"}
                      </span>
                    </div>

                    <div
                      className={styles.siteProgressBar}
                      style={{ backgroundColor: statusInfo.color }}
                    />
                  </button>
                  <span id={openHintId} className={styles.srOnly}>
                    Press Enter to open site details
                  </span>
                </React.Fragment>
              );
            })}
          </div>

          <div className="mt-2">
            <Pagination
              itemsPerPage={6}
              length={totalElements}
              currentPage={pageNumber + 1}
              updateCurrentPage={(pn) => onPageChange(pn - 1)}
            />
          </div>
        </div>

        <LeafletMapDetails
          locations={locations}
          height={620}
          sidebarOffset={360}
          selectedSite={
            selectedSolarPlant
              ? {
                  id: selectedSolarPlant.plantId,
                  latitude:
                    selectedSolarPlant.latitude ||
                    selectedSolarPlant.data?.latitude ||
                    0,
                  longitude:
                    selectedSolarPlant.longitude ||
                    selectedSolarPlant.data?.longitude ||
                    0,
                  address: selectedSolarPlant.address ?? "",
                  value: selectedSolarPlant.capacity ?? 0,
                }
              : undefined
          }
          onLocationSelect={(location) => {
            const plant = solarPlantList.find((p) => p.plantId === location.id);
            if (plant) {
              setSelectedSolarPlant(plant);
            }
          }}
          onLocationClick={(location) => {
            const plant = solarPlantList.find((p) => p.plantId === location.id);
            if (plant) {
              openSite(plant);
            }
          }}
        />
      </div>
    </div>
  );
};

export default SitesMapView;
